/*
  Helpers criptográficos isomórficos que el protocolo de emparejamiento
  por QR necesita: codificación base64url y derivación del idCliente.

  Por qué viven aquí y no se importan del Vertical: la máquina de
  emparejamiento es Global (comun/) y no puede depender de ningún
  proyecto. Estos helpers son genéricos (no contienen lógica de pss);
  pss mantiene su propia copia en `@pss/comun` por ahora, con 8+
  consumidores propios. Unificar ambos en una única fuente cripto de
  comun es un refactor mayor independiente (ver ADR de la promoción).

  Compatibilidad de wire: `derivarIdCliente` usa BLAKE3 con longitud de
  32 bytes (64 hex), idéntico a la derivación de pss — un QR emitido por
  pss y leído por un consumidor de comun (o viceversa) deriva el mismo
  idCliente.
*/

import { blake3 } from '@noble/hashes/blake3'

import type { Bytes } from './identidad'

/* Longitud en bytes del idCliente derivado de la clave pública vía
   BLAKE3. 32 bytes (256 bits) = 64 caracteres hex. */
export const LONGITUD_ID_CLIENTE_BYTES = 32

export function bytesAHex(bytes: Bytes): string {
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('')
}

export function bytesABase64Url(bytes: Bytes): string {
  let binario = ''
  for (let i = 0; i < bytes.length; i++) binario += String.fromCharCode(bytes[i]!)
  const base64 = typeof btoa === 'function'
    ? btoa(binario)
    : Buffer.from(bytes).toString('base64')
  return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

export function base64UrlABytes(b64u: string): Bytes {
  const padding = (4 - (b64u.length % 4)) % 4
  const base64 = b64u.replace(/-/g, '+').replace(/_/g, '/') + '='.repeat(padding)
  if (typeof atob === 'function') {
    const binario = atob(base64)
    const out = new Uint8Array(binario.length)
    for (let i = 0; i < binario.length; i++) out[i] = binario.charCodeAt(i)
    return out
  }
  return new Uint8Array(Buffer.from(base64, 'base64'))
}

/*
  Deriva el idCliente a partir de la clave pública de firma (Ed25519).
  Patrón infohash: la identidad no se asigna, se computa. Devuelve hex
  de 64 caracteres (32 bytes).
*/
export function derivarIdCliente(clavePublicaFirma: Bytes): string {
  const hash = blake3(clavePublicaFirma, { dkLen: LONGITUD_ID_CLIENTE_BYTES })
  return bytesAHex(hash)
}
